import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
  TextField,
  Divider,
  useMediaQuery,
  useTheme,
  Autocomplete,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Tabs,
  Tab,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  PersonAdd as PersonAddIcon,
  CloudUpload as UploadIcon,
  Description as DocIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  ConfirmationNumber as TicketIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { can } from '../../utils/roleUtils';
import ApiService from '../../services/api.service';
import { splitBySize, oversizeMessage } from '../../utils/fileSizeLimit';
import { resolveExtractedDocNumber } from '../../utils/extractionUtils';
import { filesFromEvent } from '../../utils/dropPasteFiles';
import { isPrimaryDocType, splitDocTypes } from '../../utils/docTypeGroups';
import EmbeddedTicketDialog from '../CreateTicket/EmbeddedTicketDialog';

// Font size constants
const fontSize = {
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
};

// Create initial customer object
const createCustomer = (index = 0) => ({
  id: Date.now() + index,
  name: '',
  passportNumber: '',
  emiratesId: '',
  // Optional contact details — sent to Thaklis with the employee, not stored in Managers.
  phone: '',
  email: '',
  services: [],
  documents: [],
  // Client-service custom field values shared across this applicant's services, keyed by fieldId;
  // distributed to each line's own service on save (provider-service values stay per line).
  fieldValues: {}
});

// Merge the client custom fields of all an applicant's services into one deduped list (by fieldId).
// Required if ANY service that carries the field marks it required.
const mergeClientFields = (customer) => {
  const map = new Map();
  (customer?.services || []).forEach(svc => {
    (svc.customFields || []).forEach(f => {
      const key = f.fieldId || f.code;
      if (!key) return;
      const ex = map.get(key);
      if (ex) { if (f.required) ex.required = true; }
      else map.set(key, { ...f, fieldId: f.fieldId || f.code, required: !!f.required });
    });
  });
  return Array.from(map.values());
};

// Merge the client required-document rules of all an applicant's services (deduped by code).
const mergeClientDocs = (customer) => {
  const map = new Map();
  (customer?.services || []).forEach(svc => {
    (svc.documentRequirements || []).forEach(r => {
      if (!r.code) return;
      const ex = map.get(r.code);
      if (ex) { if (r.required) ex.required = true; }
      else map.set(r.code, { ...r, required: !!r.required });
    });
  });
  return Array.from(map.values());
};

// Does an applicant document satisfy a required-doc rule (match by code or label, case-insensitive)?
const docMatchesReq = (doc, r) => {
  const t = String(doc.documentType || doc.document_type || '').trim().toLowerCase();
  return t === String(r.code || '').toLowerCase() || t === String(r.label || '').toLowerCase();
};

// Per line, resolve which applicant doc satisfies each provider required-doc slot: an explicit manual
// pick (docId), an explicit clear (null → unassigned), else the first matching unused applicant doc.
// A slot is also covered by a manual upload of that type. Returns { picks, assignedIds }.
const providerDocPlan = (line, custDocs) => {
  const reqs = line?.providerService?.documentRequirements || [];
  const manual = line?.providerReqPick || {};
  const staged = new Set((line?.providerStagedDocs || []).map(d => d.documentType));
  const picks = {};
  const assignedIds = new Set();
  reqs.forEach(r => {
    if (Object.prototype.hasOwnProperty.call(manual, r.code)) {
      if (manual[r.code]) { picks[r.code] = manual[r.code]; assignedIds.add(manual[r.code]); }
      return; // null = explicitly unassigned (suppresses the auto-match)
    }
    if (staged.has(r.code)) return; // covered by a manual upload
    const match = (custDocs || []).find(d => !assignedIds.has(d.id) && docMatchesReq(d, r));
    if (match) { picks[r.code] = match.id; assignedIds.add(match.id); }
  });
  return { picks, assignedIds };
};

const CreateOnBehalfWorkOrder = ({ currentUser }) => {
  const navigate = useNavigate();
  const { workOrderId } = useParams();
  const isEditMode = false; // on-behalf page is create-only
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // On-behalf: a provider picks one of its connected client TCs; the WO is created in that client's
  // tenant (walk-in) and delegated back to the provider. The CLIENT's Thaklis-features flag (not the
  // provider's) drives doc/identifier tracking.
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [providerServices, setProviderServices] = useState([]);
  const [providerPicker, setProviderPicker] = useState({ open: false, serviceRowId: null });
  // Working copy of the line's provider selection while the dialog is open (service + field values +
  // required-doc picks + manual uploads + which applicant docs to also send). Committed on Save.
  const [pickerDraft, setPickerDraft] = useState({ service: null, fieldValues: {}, reqPick: {}, stagedDocs: [], deselectedDocIds: [] });
  const thaklisDisabled = selectedConnection ? selectedConnection.client_thaklis_enabled !== true : true;
  const canCreateTicket = can(currentUser, 'thaklis_features', 'write');

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(isEditMode);

  // Form states — organization (a client internal org) or walk-in, like the normal create page.
  const [customerType, setCustomerType] = useState('organization');
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  // Confirmation before wiping entered data when the client, customer type, or client org changes
  // (each changes which services are available). { apply, title, message } or null.
  const [pendingReset, setPendingReset] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  // Mapped Thaklis organization (which Thaklis org the applicants belong to) —
  // same picker as the invoice-create page; always shown, both customer types.
  const [mappedOrganizations, setMappedOrganizations] = useState([]);
  const [selectedMappedOrg, setSelectedMappedOrg] = useState(null);
  const [loadingMappedOrgs, setLoadingMappedOrgs] = useState(false);

  // Confirm dialog when some applicants have no unique identifier
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Sync progress feedback
  const [syncing, setSyncing] = useState(false);
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [expectedCompletionDate, setExpectedCompletionDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  });
  const [notes, setNotes] = useState('');
  // Optional short label shown in the work order list and detail (max 50).
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  // Contact info
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');

  // Tab-based customers (each customer has their own services and documents)
  const [customers, setCustomers] = useState([createCustomer(0)]);
  const [activeTab, setActiveTab] = useState(0);

  // Queue of uploaded docs whose OCR-detected name differs from the customer already on
  // that tab. Each entry drives a "detected applicant is different" prompt (head of queue).
  const [pendingMismatches, setPendingMismatches] = useState([]);

  // Available services and document types
  const [availableServices, setAvailableServices] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);

  // KAN-29: whether Thaklis services are offered on work orders for this TC (Work Order Settings —
  // default off). When off, only internal services are selectable. When on, internal services are on
  // the first tab and Thaklis services on the second. Also gated by the TC's Thaklis-features toggle.
  const [thaklisServicesEnabled, setThaklisServicesEnabled] = useState(false);
  const [serviceTab, setServiceTab] = useState(0);
  const showThaklisServices = !thaklisDisabled && thaklisServicesEnabled;

  // Per-customer form state
  const [selectedServiceToAdd, setSelectedServiceToAdd] = useState(null);
  const [markupAmount, setMarkupAmount] = useState('');
  const [selectedDocType, setSelectedDocType] = useState('');
  const [dragActive, setDragActive] = useState(false);

  // In-place ticket creation from a Thaklis service line (create mode only)
  const [ticketDialog, setTicketDialog] = useState({
    open: false, serviceItem: null, customerId: null, prefillName: ''
  });

  // Summary sidebar expanded state
  const [summaryExpanded, setSummaryExpanded] = useState(true);

  // Fetch data on mount: document types + the provider's connected clients.
  useEffect(() => {
    fetchDocumentTypes();
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the client's services (to add) + the provider's corresponding services (per line) whenever
  // a client is selected.
  useEffect(() => {
    if (selectedConnection) {
      fetchServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnection, customerType, selectedOrganization]);

  // Fetch work order in edit mode
  useEffect(() => {
    if (isEditMode && workOrderId && organizations.length > 0) {
      fetchWorkOrder();
    }
  }, [isEditMode, workOrderId, organizations]);

  const fetchWorkOrder = async () => {
    setLoading(true);
    try {
      const response = await ApiService.getWorkOrderById(workOrderId);
      const workOrder = response?.data?.data || response?.data;

      if (workOrder) {
        const orgId = workOrder.organization_id || workOrder.organizationId;
        const org = organizations.find(o => o.id === orgId);
        if (org) setSelectedOrganization(org);

        const mappedOrgId = workOrder.mapped_organization_id || workOrder.mappedOrganizationId;
        const mappedOrgName = workOrder.mapped_organization_name || workOrder.mappedOrganizationName;
        if (mappedOrgId) {
          setSelectedMappedOrg(prev => prev && prev.id === mappedOrgId
            ? prev
            : { id: mappedOrgId, name: mappedOrgName || '', code: '' });
        }

        setOrderDate(workOrder.order_date || workOrder.orderDate || new Date().toISOString().split('T')[0]);
        setExpectedCompletionDate(workOrder.expected_completion_date || workOrder.expectedCompletionDate || '');
        setNotes(workOrder.notes || '');
        setDescription(workOrder.description || '');

        const hasOrganization = workOrder.organization_id || workOrder.organizationId;
        setCustomerType(hasOrganization ? 'organization' : 'walkin');
        setCustomerEmail(workOrder.customer_email || workOrder.customerEmail || '');
        setCustomerPhone(workOrder.customer_phone || workOrder.customerPhone || '');

        // Load customers with their services and documents
        const woCustomers = workOrder.customers || [];
        const woLineItems = workOrder.line_items || workOrder.lineItems || [];
        const woDocuments = workOrder.documents || [];

        if (woCustomers.length > 0) {
          const loadedCustomers = woCustomers.map((c, idx) => ({
            id: c.id || Date.now() + idx,
            name: c.name || c.customer_name || '',
            passportNumber: c.passportNumber || c.passport_number || '',
            emiratesId: c.emiratesId || c.emirates_id || '',
            services: woLineItems.filter(item => item.customer_id === c.id || item.customerId === c.id).map((item, i) => ({
              id: item.id || `temp-${Date.now()}-${i}`,
              service_id: item.service_id || item.serviceId,
              service_name: item.service_name || item.serviceName,
              service_description: item.service_description || item.serviceDescription || '',
              quantity: item.quantity || 1,
              unit_price: item.unit_price || item.unitPrice || 0,
              service_charge: item.service_charge || item.serviceCharge || 0,
              govt_charge: item.govt_charge || item.govtCharge || 0,
              tax_value: item.tax_value || item.taxValue || 0,
              line_total: item.line_total || item.lineTotal || 0,
              markup_amount: item.markup_amount || item.markupAmount || 0,
              is_custom_service: item.is_custom_service || item.isCustomService || false,
              service_type: item.service_type || item.serviceType || 'INTERNAL'
            })),
            documents: woDocuments.filter(d => d.customer_id === c.id || d.customerId === c.id).map(d => ({
              id: d.id || Date.now(),
              fileName: d.file_name || d.fileName,
              fileSize: d.file_size || d.fileSize || 0,
              documentType: d.document_type || d.documentType,
              documentTypeName: d.document_type_name || d.documentTypeName || d.document_type
            }))
          }));
          setCustomers(loadedCustomers);
        } else if (woLineItems.length > 0) {
          // No customers[] array on the response — reconstruct from the
          // per-line-item customer_name / customer_id_number. Line items with
          // the same customer_name collapse into one customer record.
          const byName = new Map();
          woLineItems.forEach((item, i) => {
            const name = item.customer_name || item.customerName || '';
            if (!byName.has(name)) {
              byName.set(name, {
                id: Date.now() + byName.size,
                name,
                passportNumber: item.customer_id_number || item.customerIdNumber || '',
                services: [],
                documents: []
              });
            }
            byName.get(name).services.push({
              id: item.id || `temp-${Date.now()}-${i}`,
              service_id: item.service_id || item.serviceId,
              service_name: item.service_name || item.serviceName,
              service_description: item.service_description || item.serviceDescription || '',
              quantity: item.quantity || 1,
              service_charge: item.service_charge || item.serviceCharge || 0,
              govt_charge: item.govt_charge || item.govtCharge || 0,
              tax_value: item.tax_value || item.taxValue || 0,
              line_total: item.line_total || item.lineTotal || 0,
              markup_amount: item.markup_amount || item.markupAmount || 0,
              service_type: item.service_type || item.serviceType || 'INTERNAL'
            });
          });
          // Attach documents by customer_name
          woDocuments.forEach(d => {
            const name = d.customer_name || d.customerName || '';
            if (byName.has(name)) {
              byName.get(name).documents.push({
                id: d.id || Date.now(),
                fileName: d.file_name || d.fileName,
                fileSize: d.file_size || d.fileSize || 0,
                documentType: d.document_type || d.documentType,
                documentTypeName: d.document_type_name || d.documentTypeName || d.document_type
              });
            }
          });
          if (byName.size > 0) {
            setCustomers(Array.from(byName.values()));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching work order:', err);
      setError('Failed to load work order');
    } finally {
      setLoading(false);
    }
  };

  const fetchMappedOrganizations = async () => {
    setLoadingMappedOrgs(true);
    try {
      const typingCenterId = currentUser?.typingCenterId || currentUser?.typing_center_id;
      const response = await ApiService.getMappings(typingCenterId);
      const orgs = (response?.data?.data || []).map(m => ({
        id: m.organization_id,
        name: m.organization_name,
        code: m.organization_code || '',
        // mapping id (typing_center_organization_mapping.id) — needed to init the
        // Thaklis client for in-place ticket creation from a WO line.
        mappingId: m.id || m.mappingId
      }));
      setMappedOrganizations(orgs);
      // Auto-select when exactly one mapping (mirrors invoice-create behavior).
      if (orgs.length === 1) {
        setSelectedMappedOrg(orgs[0]);
      }
    } catch (err) {
      console.error('Error fetching mapped organizations:', err);
    } finally {
      setLoadingMappedOrgs(false);
    }
  };

  const loadClients = async () => {
    setLoadingClients(true);
    try {
      const res = await ApiService.getOnBehalfClients();
      setClients(res?.data?.data || res?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load connected clients');
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  // Normalize a resolved-service row (snake_case from the on-behalf endpoints) to the shape the
  // service Autocomplete/table expects. On-behalf services are always internal (delegatable).
  const mapService = (s) => ({
    id: s.id,
    name: s.name || s.service_name,
    description: s.description || '',
    service_charge: s.service_charge || s.serviceCharge || 0,
    tax_on_service_charge: s.tax_on_service_charge || s.taxOnServiceCharge || 0,
    govt_charge: s.govt_charge || s.govtCharge || 0,
    type: 'INTERNAL',
    createsTask: s.createsTask || s.creates_task || false,
    // Custom fields (value pre-filled from org defaults) + required documents, kept for both the
    // client-service (create) and provider-service (delegate) sections.
    customFields: (s.customFields || s.custom_fields || []).map(f => ({
      fieldId: f.fieldId || f.tcCustomFieldId || f.id,
      code: f.code,
      label: f.label,
      fieldType: f.fieldType || f.field_type || 'TEXT',
      options: f.options || [],
      required: f.required === true,
      orgDefault: f.orgDefault ?? f.value ?? ''
    })),
    documentRequirements: (s.documentRequirements || s.document_requirements || []).map(r => ({
      code: r.code,
      label: r.label,
      required: r.required !== false
    }))
  });

  // Seed a {fieldId: value} map from a service's custom fields' org defaults.
  const seedFieldValues = (svc) => {
    const seeded = {};
    (svc?.customFields || []).forEach(f => { seeded[f.fieldId] = f.orgDefault || ''; });
    return seeded;
  };

  const fetchServices = async () => {
    if (!selectedConnection) return;
    const orgId = customerType === 'organization' ? (selectedOrganization?.id || null) : null;
    try {
      const [cs, ps] = await Promise.all([
        ApiService.getOnBehalfClientServices(selectedConnection.connection_id, orgId),
        ApiService.getOnBehalfProviderServices(selectedConnection.connection_id)
      ]);
      setAvailableServices((cs?.data?.data || cs?.data || []).map(mapService));
      setProviderServices((ps?.data?.data || ps?.data || []).map(mapService));
    } catch (err) {
      console.error('Error fetching services:', err);
      setAvailableServices([]);
      setProviderServices([]);
    }
  };

  // True when the applicant tabs hold anything worth warning about before a reset.
  const hasEnteredData = () => customers.some(c => (c.services?.length || 0) > 0 || (c.documents?.length || 0) > 0);
  // Run a reset immediately, or ask first when there's entered data to lose.
  const guardedReset = (apply, title, message) => {
    if (hasEnteredData()) { setPendingReset({ apply, title, message }); return; }
    apply();
  };

  const applySelectConnection = (conn) => {
    setSelectedConnection(conn);
    setCustomers([createCustomer(0)]);
    setActiveTab(0);
    setAvailableServices([]);
    setProviderServices([]);
    setSelectedServiceToAdd(null);
    setSelectedOrganization(null);
    setCustomerType('organization');
    setCustomerEmail('');
    setCustomerPhone('');
    setError(null);
    setSuccess(null);
    if (conn) {
      // Load the client TC's own internal organizations (customers) for the organization option.
      setLoadingOrgs(true);
      ApiService.getOnBehalfClientOrganizations(conn.connection_id)
        .then(res => setOrganizations((res?.data?.data || res?.data || []).map(o => ({ id: o.id, name: o.name, type: o.type }))))
        .catch(() => setOrganizations([]))
        .finally(() => setLoadingOrgs(false));
    } else {
      setOrganizations([]);
    }
  };

  // Changing the client (connection) resets available services + everything entered — confirm first.
  const handleSelectConnection = (conn) => {
    if ((conn?.connection_id || null) === (selectedConnection?.connection_id || null)) return;
    guardedReset(() => applySelectConnection(conn), 'Change client?',
      'Switching the client will clear all entered customers, added services, uploaded documents, and details for this work order. Continue?');
  };

  // ---- provider-service picker dialog (per service line): pick the provider service and fill its
  //      custom fields + required documents here, then Save back to the line. ----
  const openProviderPicker = (serviceRowId) => {
    const cust = customers[activeTab];
    const line = cust?.services.find(s => s.id === serviceRowId);
    setPickerDraft({
      service: line?.providerService || null,
      fieldValues: { ...(line?.providerFieldValues || {}) },
      reqPick: { ...(line?.providerReqPick || {}) },
      stagedDocs: [...(line?.providerStagedDocs || [])],
      deselectedDocIds: [...(line?.providerDeselectedDocIds || [])],
    });
    setProviderPicker({ open: true, serviceRowId });
  };
  const closeProviderPicker = () => setProviderPicker({ open: false, serviceRowId: null });

  // Pick the provider service inside the dialog: seed its field defaults and reset doc picks/uploads.
  const pickerSelectService = (ps) =>
    setPickerDraft(d => ({ ...d, service: ps, fieldValues: seedFieldValues(ps), reqPick: {}, stagedDocs: [] }));
  const pickerSetField = (fieldId, value) =>
    setPickerDraft(d => ({ ...d, fieldValues: { ...d.fieldValues, [fieldId]: value } }));
  const pickerPickReqDoc = (reqCode, docId) =>
    setPickerDraft(d => ({ ...d, reqPick: { ...d.reqPick, [reqCode]: docId } }));
  // Clear a slot by explicitly unassigning it (null) so the auto-match doesn't immediately refill it.
  const pickerClearReqDoc = (reqCode) =>
    setPickerDraft(d => ({ ...d, reqPick: { ...d.reqPick, [reqCode]: null } }));
  const pickerStageDoc = (documentType, fileList) => {
    const { ok: files, tooBig } = splitBySize(Array.from(fileList || []));
    if (tooBig.length) setError(tooBig.map(oversizeMessage).join(' '));
    if (!files.length) return;
    setPickerDraft(d => ({ ...d, stagedDocs: [...d.stagedDocs,
      ...files.map((file, i) => ({ tempId: `${Date.now()}-${i}-${file.name}`, file, documentType, fileName: file.name }))] }));
  };
  const pickerRemoveStaged = (tempId) =>
    setPickerDraft(d => ({ ...d, stagedDocs: d.stagedDocs.filter(s => s.tempId !== tempId) }));
  const pickerToggleExtraDoc = (docId) =>
    setPickerDraft(d => ({ ...d, deselectedDocIds: d.deselectedDocIds.includes(docId)
      ? d.deselectedDocIds.filter(id => id !== docId)
      : [...d.deselectedDocIds, docId] }));

  // Commit the dialog draft back to the line.
  const saveProviderPicker = () => {
    const rowId = providerPicker.serviceRowId;
    const cust = customers[activeTab];
    if (cust) {
      setCustomers(prev => prev.map(c => c.id === cust.id
        ? { ...c, services: c.services.map(s => (s.id === rowId
            ? { ...s,
                providerService: pickerDraft.service,
                providerFieldValues: pickerDraft.fieldValues,
                providerReqPick: pickerDraft.reqPick,
                providerStagedDocs: pickerDraft.stagedDocs,
                providerDeselectedDocIds: pickerDraft.deselectedDocIds } : s)) }
        : c));
    }
    closeProviderPicker();
  };

  // Client custom field values are shared per applicant (keyed by fieldId), entered once and
  // distributed to each client line on save.
  const handleClientFieldChange = (fieldId, value) => {
    const cust = customers[activeTab];
    setCustomers(prev => prev.map(c => c.id === cust.id
      ? { ...c, fieldValues: { ...(c.fieldValues || {}), [fieldId]: value } } : c));
  };

  // Render one custom-field input (SELECT / DATE / NUMBER / TEXTAREA / text).
  const renderFieldInput = (f, value, onChange) => {
    const lbl = f.label + (f.required ? ' *' : '');
    if (f.fieldType === 'SELECT') {
      return (
        <TextField select fullWidth size="small" label={lbl} value={value}
          onChange={(e) => onChange(e.target.value)}>
          <MenuItem value=""><em>None</em></MenuItem>
          {(f.options || []).map((opt, i) => (
            <MenuItem key={i} value={typeof opt === 'string' ? opt : (opt.value || '')}>
              {typeof opt === 'string' ? opt : (opt.label || opt.value || '')}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    return (
      <TextField fullWidth size="small" label={lbl} value={value}
        type={f.fieldType === 'NUMBER' ? 'number' : f.fieldType === 'DATE' ? 'date' : 'text'}
        multiline={f.fieldType === 'TEXTAREA'} rows={f.fieldType === 'TEXTAREA' ? 2 : undefined}
        InputLabelProps={f.fieldType === 'DATE' ? { shrink: true } : undefined}
        onChange={(e) => onChange(e.target.value)} />
    );
  };

  // Auto-match the provider service by name (mirrors the WO-detail delegation name match). Case- and
  // whitespace-insensitive; returns the matching provider service or null.
  const normalizeName = (s) => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const matchProviderService = (name) => {
    const target = normalizeName(name);
    if (!target) return null;
    return providerServices.find(ps => normalizeName(ps.name) === target) || null;
  };

  const fetchWorkOrderConfig = async () => {
    try {
      const tcId = currentUser?.typingCenterId || currentUser?.typing_center_id;
      if (!tcId) return;
      const resp = await ApiService.getWorkOrderConfig(tcId);
      const c = resp?.data?.data || resp?.data || {};
      setThaklisServicesEnabled(c.thaklis_services_enabled === true);
    } catch (err) {
      // Default to off (internal services only) if the config can't be read.
      setThaklisServicesEnabled(false);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      // Prefer this typing center's own document types; fall back to the Thaklis
      // standard list only when the TC hasn't defined any yet.
      const tcDocTypes = await import('../../services/tc-document-type.service')
        .then(m => m.default.getActive()).catch(() => []);
      if (tcDocTypes.length > 0) {
        const categoryOrder = ['Identity', 'Immigration', 'Medical', 'Employment', 'Insurance', 'Company', 'Financial', 'Legal', 'Other'];
        const sorted = [...tcDocTypes].sort((a, b) => {
          const ai = categoryOrder.indexOf(a.category || 'Other');
          const bi = categoryOrder.indexOf(b.category || 'Other');
          if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
          return (a.sortOrder || 0) - (b.sortOrder || 0);
        });
        setDocumentTypes(sorted.map(dt => ({ code: dt.code, label: dt.label, category: dt.category, holderType: dt.holder_type || dt.holderType, isPrimary: isPrimaryDocType(dt) })));
      } else {
        const response = await ApiService.getDocumentTypes();
        setDocumentTypes(response.data?.data || []);
      }
    } catch (err) {
      console.error('Error fetching document types:', err);
      setDocumentTypes([]);
    }
  };

  // Tab management
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Reset per-tab form state
    setSelectedServiceToAdd(null);
    setMarkupAmount('');
    setSelectedDocType('');
  };

  const handleAddCustomer = () => {
    const newCustomer = createCustomer(customers.length);
    setCustomers(prev => [...prev, newCustomer]);
    setActiveTab(customers.length); // Switch to new tab
  };

  const handleRemoveCustomer = (customerId) => {
    if (customers.length === 1) {
      setError('At least one customer is required');
      return;
    }
    const customerIndex = customers.findIndex(c => c.id === customerId);
    setCustomers(prev => prev.filter(c => c.id !== customerId));
    // Adjust active tab if needed
    if (activeTab >= customers.length - 1) {
      setActiveTab(Math.max(0, customers.length - 2));
    } else if (activeTab > customerIndex) {
      setActiveTab(activeTab - 1);
    }
  };

  const handleCustomerChange = (customerId, field, value) => {
    setCustomers(prev => prev.map(c =>
      c.id === customerId ? { ...c, [field]: value } : c
    ));
  };

  // Client organization change — resets entered data (prices/fields/services are org-specific).
  const applyOrganizationChange = (value) => {
    setSelectedOrganization(value);
    setCustomerEmail(value?.email || '');
    setCustomerPhone(value?.phone || '');
    setCustomers([createCustomer(0)]);
    setActiveTab(0);
    setSelectedServiceToAdd(null);
    setMarkupAmount('');
    setSelectedDocType('');
  };
  const handleOrganizationChange = (event, value) => {
    if ((value?.id || null) === (selectedOrganization?.id || null)) return;
    guardedReset(() => applyOrganizationChange(value), 'Change organization?',
      'Switching the organization will clear all entered customers, added services, uploaded documents, and details for this work order. Continue?');
  };

  // Customer-type change (walk-in ↔ organization) — also resets entered data.
  const applyCustomerTypeChange = (type) => {
    setCustomerType(type);
    setSelectedOrganization(null);
    setCustomerEmail('');
    setCustomerPhone('');
    setCustomers([createCustomer(0)]);
    setActiveTab(0);
    setSelectedServiceToAdd(null);
    setMarkupAmount('');
    setSelectedDocType('');
  };
  const handleCustomerTypeChange = (type) => {
    if (type === customerType) return;
    guardedReset(() => applyCustomerTypeChange(type), 'Change customer type?',
      'Available services and prices differ between walk-in and organization customers. Switching will clear all entered customers, added services, uploaded documents, and details for this work order. Continue?');
  };

  // Service management - Add service to current customer
  const handleAddServiceToCustomer = () => {
    if (!selectedServiceToAdd) {
      setError('Please select a service');
      return;
    }

    const service = selectedServiceToAdd;
    // Services already return exclusive service_charge and separate taxOnServiceCharge
    const serviceCharge = service.service_charge || 0;
    const serviceTax = service.tax_on_service_charge || 0;
    const govtCharge = service.govt_charge || 0;
    // Markup entered by user is inclusive of VAT — split using same rounding as invoice
    const markupInclVat = parseFloat(markupAmount) || 0;
    const markupBase = Number((markupInclVat / 1.05).toFixed(2));
    const markupVat = Number((markupInclVat - markupBase).toFixed(2));
    // line_total = all-inclusive (sc + gc + markup + tax + taxOnMarkup)
    const lineTotal = serviceCharge + govtCharge + markupBase + serviceTax + markupVat;
    // original_line_total = base with tax, without markup
    const originalLineTotal = serviceCharge + govtCharge + serviceTax;

    const newService = {
      id: `temp-${Date.now()}`,
      service_id: service.id,
      service_name: service.name,
      service_description: service.description || '',
      quantity: 1,
      unit_price: serviceCharge + govtCharge,
      service_charge: serviceCharge,
      govt_charge: govtCharge,
      tax_value: serviceTax,
      tax_on_markup: markupVat,
      line_total: lineTotal,
      original_line_total: originalLineTotal,
      markup_amount: markupBase,
      is_custom_service: service.type === 'INTERNAL',
      service_type: service.type || 'THAKLIS',
      creates_task: service.createsTask || false,
      // The provider's corresponding service; auto-matched by name on add, still changeable per line.
      providerService: matchProviderService(service.name),
      // Client-service custom fields/docs (create side). Values live on the applicant (merged), not here.
      customFields: service.customFields || [],
      documentRequirements: service.documentRequirements || [],
      // Provider-service custom field values (delegate side), seeded from the matched provider service.
      providerFieldValues: seedFieldValues(matchProviderService(service.name)),
      // Manual provider documents staged for this line: [{tempId, file, documentType, fileName}].
      providerStagedDocs: [],
      // Applicant docs the user UN-checked for this line's provider WO (default: all selected).
      providerDeselectedDocIds: [],
      // Explicit provider required-doc slot assignments (reqCode -> applicant docId, or null = cleared).
      providerReqPick: {}
    };

    const currentCustomer = customers[activeTab];
    setCustomers(prev => prev.map(c => {
      if (c.id !== currentCustomer.id) return c;
      // Seed the applicant's shared client field values from this service's org defaults (don't
      // overwrite anything already filled).
      const nextFieldValues = { ...(c.fieldValues || {}) };
      (newService.customFields || []).forEach(f => {
        const def = f.orgDefault;
        const cur = nextFieldValues[f.fieldId];
        if (def != null && String(def).trim() !== '' && (cur == null || String(cur).trim() === '')) {
          nextFieldValues[f.fieldId] = def;
        }
      });
      return { ...c, services: [...c.services, newService], fieldValues: nextFieldValues };
    }));

    setSelectedServiceToAdd(null);
    setMarkupAmount('');
  };

  const handleRemoveServiceFromCustomer = (serviceId) => {
    const currentCustomer = customers[activeTab];
    setCustomers(prev => prev.map(c =>
      c.id === currentCustomer.id
        ? { ...c, services: c.services.filter(s => s.id !== serviceId) }
        : c
    ));
  };

  // --- In-place ticket creation for Thaklis service lines -------------------
  const handleOpenTicketDialog = (item) => {
    if (!selectedMappedOrg?.mappingId) {
      setError('Select the mapped organization before creating a ticket.');
      return;
    }
    const cust = customers[activeTab];
    setTicketDialog({
      open: true,
      serviceItem: item,
      customerId: cust.id,
      prefillName: cust.name?.trim() || ''
    });
  };

  const handleCloseTicketDialog = () => {
    setTicketDialog({ open: false, serviceItem: null, customerId: null, prefillName: '' });
  };

  // The embedded ticket form hands back the created ticket. Re-fetch it through the
  // managers ticket list (same shape the manual "Link Ticket" flow uses) so the stored
  // snapshot carries charges/customerName for the later WO→invoice conversion, then
  // stamp it onto the line item so submit can auto-link it.
  const handleTicketCreated = async (createdTicket) => {
    const { serviceItem, customerId } = ticketDialog;
    if (!serviceItem) { handleCloseTicketDialog(); return; }

    const ticketId = createdTicket?.id || createdTicket?.data?.id;
    let snapshot = createdTicket?.data || createdTicket;
    let ticketNumber = createdTicket?.orgTicketNumber || createdTicket?.org_ticket_number
      || createdTicket?.ticketNumber || createdTicket?.ticket_number
      || createdTicket?.data?.ticketNumber;

    try {
      const typingCenterId = currentUser?.typingCenterId || currentUser?.typing_center_id;
      if (typingCenterId && ticketId && serviceItem.service_id) {
        const resp = await ApiService.getTickets({ typingCenterId, serviceId: serviceItem.service_id });
        const list = resp?.data?.data || resp?.data || [];
        const match = list.find(t => String(t.id) === String(ticketId));
        if (match) {
          snapshot = match;
          ticketNumber = match.orgTicketNumber || match.org_ticket_number
            || match.ticketNumber || match.ticket_number || ticketNumber;
        }
      }
    } catch (err) {
      console.error('Could not re-fetch created ticket for snapshot, using create payload:', err);
    }

    setCustomers(prev => prev.map(c =>
      c.id === customerId
        ? {
            ...c,
            services: c.services.map(s => s.id === serviceItem.id
              ? { ...s, created_ticket: { id: ticketId, number: ticketNumber, snapshot } }
              : s)
          }
        : c
    ));
    setSuccess(`Ticket ${ticketNumber || ''} created — it will be linked when the work order is created.`);
    handleCloseTicketDialog();
  };

  const handleRemoveCreatedTicket = (customerId, serviceItemId) => {
    setCustomers(prev => prev.map(c =>
      c.id === customerId
        ? { ...c, services: c.services.map(s => s.id === serviceItemId ? { ...s, created_ticket: null } : s) }
        : c
    ));
  };

  // Document management — supports selecting multiple files at once. Each file is
  // staged as its own document (same type) and OCR-extracted individually; on sync,
  // files of the same document type are attached together in one bulk batch.
  // `forced` ({ code, label }) is passed by a client required-document slot so the upload targets that
  // specific doc type; without it the upload uses the "add another document" type dropdown.
  const handleFileUpload = async (event, forced = null) => {
    const picked = Array.from(event.target.files || []);
    if (picked.length === 0) return;
    const { ok: files, tooBig } = splitBySize(picked);
    if (tooBig.length) setError(tooBig.map(oversizeMessage).join(' '));
    if (files.length === 0) return;

    const useDocType = forced?.code || selectedDocType;
    if (!useDocType) {
      setError('Please select a document type');
      return;
    }

    const docType = documentTypes.find(d => (d.code || d.id) === useDocType);
    const currentCustomer = customers[activeTab];
    const baseId = Date.now();

    const stagedDocs = files.map((file, i) => ({
      id: baseId + i,
      file,
      fileName: file.name,
      fileSize: file.size,
      documentType: useDocType,
      documentTypeName: forced?.label || docType?.label || docType?.name || useDocType,
      extracting: true,
      // Extracted tracking metadata (filled after OCR)
      documentNumber: null,
      issueDate: null,
      expiryDate: null
    }));

    setCustomers(prev => prev.map(c =>
      c.id === currentCustomer.id
        ? { ...c, documents: [...c.documents, ...stagedDocs] }
        : c
    ));

    if (!forced) setSelectedDocType('');
    if (event.target) event.target.value = '';

    const clean = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).trim();
      return (!s || s.toLowerCase() === 'null') ? '' : s;
    };

    // OCR extraction (generalized prompt) per file: auto-fill applicant identity when
    // present (only into empty fields) and stash each document's own number/issue/expiry.
    await Promise.all(stagedDocs.map(async (staged) => {
      try {
        const response = await ApiService.extractDocument(staged.file, 'GENERAL', staged.documentType);
        const extracted = response?.data?.data || {};
        // Surface a clear reason when extraction didn't run (e.g. the TC's OpenAI key is not
        // configured, or Thaklis skipped it) instead of silently filling nothing. The document is
        // still staged either way — extraction is best-effort.
        const extractFailure = response?.data?.success === false
          ? (response.data.error || response.data.message)
          : (extracted._extractionSkipped
            ? (extracted._extractionSkippedReason || 'Automatic field extraction was skipped — enter the fields manually.')
            : null);
        if (extractFailure) {
          setError(extractFailure);
        }
        const fullName = clean(extracted.fullName);
        const passportNumber = clean(extracted.passportNumber);
        const emiratesIdNumber = clean(extracted.emiratesIdNumber);
        const phoneNumber = clean(extracted.phoneNumber);
        const email = clean(extracted.email);
        const documentNumber = resolveExtractedDocNumber(extracted, staged.documentType);
        const issueDate = clean(extracted.issueDate);
        const expiryDate = clean(extracted.expiryDate);

        let mismatch = null;
        setCustomers(prev => prev.map(c => {
          if (c.id !== currentCustomer.id) return c;
          const existingName = c.name?.trim() || '';
          // A name was detected that differs from the customer already on this tab —
          // don't touch the identity; queue a prompt asking whether to split it out.
          if (existingName && fullName && existingName.toLowerCase() !== fullName.toLowerCase()) {
            mismatch = {
              docId: staged.id,
              sourceCustomerId: c.id,
              existingName,
              extracted: {
                fullName,
                passportNumber,
                emiratesId: emiratesIdNumber,
                phone: phoneNumber,
                email
              }
            };
            // Clear the spinner + record this doc's own metadata; leave identity as-is.
            return {
              ...c,
              documents: c.documents.map(d => d.id === staged.id
                ? { ...d, extracting: false, documentNumber, issueDate, expiryDate }
                : d)
            };
          }
          return {
            ...c,
            // Only fill fields that are currently empty (never overwrite user input)
            name: existingName ? c.name : (fullName || c.name),
            passportNumber: c.passportNumber?.trim() ? c.passportNumber : (passportNumber || c.passportNumber),
            emiratesId: c.emiratesId?.trim() ? c.emiratesId : (emiratesIdNumber || c.emiratesId),
            phone: c.phone?.trim() ? c.phone : (phoneNumber || c.phone),
            email: c.email?.trim() ? c.email : (email || c.email),
            documents: c.documents.map(d => d.id === staged.id
              ? { ...d, extracting: false, documentNumber, issueDate, expiryDate }
              : d)
          };
        }));
        if (mismatch) {
          setPendingMismatches(prev =>
            prev.some(m => m.docId === mismatch.docId) ? prev : [...prev, mismatch]);
        }
      } catch (extractErr) {
        console.error('Document extraction failed:', extractErr);
        setError('Document extraction failed — enter the fields manually.');
        // Extraction is best-effort — clear the spinner and keep the staged file.
        setCustomers(prev => prev.map(c =>
          c.id === currentCustomer.id
            ? { ...c, documents: c.documents.map(d => d.id === staged.id ? { ...d, extracting: false } : d) }
            : c
        ));
      }
    }));
  };

  // Stage documents dragged in or pasted (Ctrl/Cmd+V of a copied image) — no download needed.
  // Runs through the same staging + OCR path as a selected file (via a synthetic input event).
  const handleAddDroppedFiles = async (e, forced = null) => {
    const files = filesFromEvent(e);
    if (!files.length) return;
    e.preventDefault();
    if (!forced && !selectedDocType) {
      setError('Please select a document type');
      return;
    }
    await handleFileUpload({ target: { files, value: '' } }, forced);
  };

  const handleRemoveDocument = (docId) => {
    const currentCustomer = customers[activeTab];
    setCustomers(prev => prev.map(c =>
      c.id === currentCustomer.id
        ? { ...c, documents: c.documents.filter(d => d.id !== docId) }
        : c
    ));
  };

  // Name-mismatch prompt — user chose to keep the doc with the existing customer.
  const handleKeepSameCustomer = () => {
    setPendingMismatches(prev => prev.slice(1));
  };

  // Name-mismatch prompt — user chose to split the detected applicant into a new
  // customer: create a tab, fill it with the extracted identity, move the doc there.
  const handleMoveToNewCustomer = () => {
    const mismatch = pendingMismatches[0];
    if (!mismatch) return;

    const newIndex = customers.length;
    const newCustomer = {
      ...createCustomer(newIndex),
      name: mismatch.extracted.fullName || '',
      passportNumber: mismatch.extracted.passportNumber || '',
      emiratesId: mismatch.extracted.emiratesId || '',
      phone: mismatch.extracted.phone || '',
      email: mismatch.extracted.email || ''
    };

    setCustomers(prev => {
      let movedDoc = null;
      const updated = prev.map(c => {
        if (c.id !== mismatch.sourceCustomerId) return c;
        movedDoc = c.documents.find(d => d.id === mismatch.docId) || null;
        return { ...c, documents: c.documents.filter(d => d.id !== mismatch.docId) };
      });
      return [...updated, { ...newCustomer, documents: movedDoc ? [movedDoc] : [] }];
    });

    setActiveTab(newIndex);
    setPendingMismatches(prev => prev.slice(1));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Calculate totals per customer
  const calculateCustomerTotal = useCallback((customer) => {
    // subtotal from components (sc + gc + markup), not from line_total (which includes tax)
    const subtotal = customer.services.reduce((sum, s) =>
      sum + (s.service_charge || 0) + (s.govt_charge || 0) + (s.markup_amount || 0), 0);
    const taxTotal = customer.services.reduce((sum, s) =>
      sum + (s.tax_value || 0) + (s.tax_on_markup || 0), 0);
    const grandTotal = subtotal + taxTotal;
    return { subtotal, taxTotal, grandTotal };
  }, []);

  // Calculate grand totals across all customers
  const calculateGrandTotals = useCallback(() => {
    let grandTotal = 0;
    let taxTotal = 0;
    let subtotal = 0;

    customers.forEach(customer => {
      const totals = calculateCustomerTotal(customer);
      grandTotal += totals.grandTotal;
      taxTotal += totals.taxTotal;
      subtotal += totals.subtotal;
    });

    return { subtotal, taxTotal, grandTotal };
  }, [customers, calculateCustomerTotal]);

  // Check if customer is complete (has name and at least one service)
  const isCustomerComplete = useCallback((customer) => {
    return customer.name.trim() && customer.services.length > 0;
  }, []);

  // A customer can only be tracked as a Thaklis employee if it has a passport or Emirates ID.
  const customerHasIdentifier = (c) => Boolean(c.passportNumber?.trim() || c.emiratesId?.trim());

  // Submit handler — validates, then runs the on-behalf create + delegate.
  const handleCreateWorkOrder = async () => {
    if (!selectedConnection) {
      setError('Please select a client');
      return;
    }
    if (customerType === 'organization' && !selectedOrganization) {
      setError("Please select the client's organization");
      return;
    }
    // Every applicant must have a name — a blank-name customer would otherwise be silently
    // dropped along with its services/documents.
    if (customers.some(c => !c.name.trim())) {
      setError('Please enter a name for every applicant');
      return;
    }
    const validCustomers = customers.filter(c => c.name.trim());
    const allSvcs = validCustomers.flatMap(c => c.services);
    if (allSvcs.length === 0) {
      setError('Please add at least one service');
      return;
    }
    if (!allSvcs.every(s => s.providerService)) {
      setError('Select a corresponding provider service for every service before submitting');
      return;
    }

    // Required custom fields and required documents must be present — both for the client service
    // (create side) and the chosen provider service (delegate side). Docs are the applicant's uploads,
    // matched by type code.
    for (const c of validCustomers) {
      const uploadedTypes = new Set((c.documents || []).map(d => d.documentType || d.document_type));
      // Client side: fields + docs merged once per applicant across their services.
      for (const f of mergeClientFields(c)) {
        if (f.required && !String(c.fieldValues?.[f.fieldId] ?? '').trim()) {
          setError(`Fill required field "${f.label}" for ${c.name}`);
          return;
        }
      }
      for (const r of mergeClientDocs(c)) {
        if (r.required && !uploadedTypes.has(r.code)) {
          setError(`Upload required document "${r.label}" for ${c.name}`);
          return;
        }
      }
      // Provider side: per line (each line's own provider service).
      for (const s of c.services) {
        const ps = s.providerService;
        if (!ps) continue;
        for (const f of (ps.customFields || [])) {
          if (f.required && !String(s.providerFieldValues?.[f.fieldId] ?? '').trim()) {
            setError(`Fill required provider field "${f.label}" for ${c.name} — ${ps.name}`);
            return;
          }
        }
        // Provider required docs: covered by an auto-filled/reused applicant doc or a manual upload.
        const { picks } = providerDocPlan(s, c.documents || []);
        const providerStagedTypes = new Set((s.providerStagedDocs || []).map(d => d.documentType));
        for (const r of (ps.documentRequirements || [])) {
          if (r.required && !picks[r.code] && !providerStagedTypes.has(r.code)) {
            setError(`Attach the required document "${r.label}" for ${c.name} — provider ${ps.name}`);
            return;
          }
        }
      }
    }

    await proceedCreateWorkOrder();
  };

  const proceedCreateWorkOrder = async () => {
    const validCustomers = customers.filter(c => c.name.trim());
    const connectionId = selectedConnection?.connection_id;
    if (!connectionId) return;

    setCreating(true);
    setError(null);

    try {
      // 1. Build line items (walk-in, one per applicant service) for the CLIENT's WO.
      const line_items = validCustomers.flatMap(customer => customer.services.map(item => ({
        service_id: item.service_id,
        service_name: item.service_name,
        service_description: item.service_description || '',
        quantity: 1,
        unit_price: item.unit_price,
        service_charge: item.service_charge,
        govt_charge: item.govt_charge,
        tax_value: item.tax_value,
        markup_amount: item.markup_amount,
        tax_on_markup: item.tax_on_markup,
        line_total: item.line_total,
        original_line_total: item.original_line_total,
        is_custom_service: true,
        customer_name: customer.name.trim(),
        customer_id_number: customer.passportNumber?.trim() || null,
        customer_emirates_id: customer.emiratesId?.trim() || null,
        // Client-service custom field values (persisted + required-validated on the client WO).
        // Distribute the applicant's shared client field values to this line's own service fields.
        field_values: (item.customFields || [])
          .map(f => ({ fieldId: f.fieldId, code: f.code, label: f.label, value: customer.fieldValues?.[f.fieldId] }))
          .filter(fv => fv.value != null && String(fv.value).trim() !== '')
      })));

      // 2. Create the WO in the client's tenant; the response carries the created line items.
      const createRes = await ApiService.createOnBehalfWorkOrder({
        connection_id: connectionId,
        customer_type: customerType,
        organization_id: customerType === 'organization' ? (selectedOrganization?.id || null) : null,
        organization_name: customerType === 'organization' ? (selectedOrganization?.name || null) : null,
        customer_email: customerEmail.trim() || null,
        customer_phone: customerPhone.trim() || null,
        title: title.trim() || null,
        line_items
      });
      const created = createRes?.data?.data || createRes?.data;
      const woId = created?.work_order_id;
      if (!woId) throw new Error('Work order id missing from response');
      const createdLines = created?.line_items || [];

      // 3. Map each created line to its chosen provider service (by service_id + applicant name).
      const providerFor = (lineServiceId, applicantName) => {
        const applicant = validCustomers.find(c => c.name.trim() === applicantName);
        const svc = applicant?.services.find(s =>
          String(s.service_id) === String(lineServiceId) && s.providerService);
        return svc?.providerService?.id || null;
      };
      const lineItemProviderServices = {};
      // Provider-service custom field values, keyed by the created line item id, for the delegate step.
      const lineItemFieldValues = {};
      const serviceFor = (lineServiceId, applicantName) => {
        const applicant = validCustomers.find(c => c.name.trim() === applicantName);
        return applicant?.services.find(s =>
          String(s.service_id) === String(lineServiceId) && s.providerService) || null;
      };
      // Manual provider documents, tagged with their created line item id (uploaded to the provider WO).
      const delegateFiles = [];
      createdLines.forEach(li => {
        const psId = providerFor(li.service_id || li.serviceId, li.customer_name || li.customerName);
        if (psId) lineItemProviderServices[li.id] = String(psId);
        const svc = serviceFor(li.service_id || li.serviceId, li.customer_name || li.customerName);
        if (svc && svc.providerService) {
          const pf = (svc.providerService.customFields || [])
            .filter(f => { const v = svc.providerFieldValues?.[f.fieldId]; return v != null && String(v).trim() !== ''; })
            .map(f => ({ fieldId: f.fieldId, code: f.code, label: f.label, value: svc.providerFieldValues[f.fieldId] }));
          if (pf.length) lineItemFieldValues[li.id] = pf;
          (svc.providerStagedDocs || []).forEach(d => {
            delegateFiles.push({ lineItemId: String(li.id), file: d.file, documentType: d.documentType });
          });
        }
      });

      // 4. Upload each applicant's staged documents (best-effort; Managers S3). Store the doc type by
      // its code, and capture the created document id so the delegate knows which applicant docs to
      // copy to each provider WO.
      const uploadedDocId = {}; // staged doc id -> created WO document id
      for (const customer of validCustomers) {
        for (const d of (customer.documents || [])) {
          if (!d.file) continue;
          try {
            const resp = await ApiService.uploadOnBehalfDocument(
              woId, connectionId, d.file, customer.name?.trim() || null,
              d.documentType || d.documentTypeName || 'APPLICATION_FORM');
            const realId = resp?.data?.data?.id;
            if (realId) uploadedDocId[d.id] = realId;
          } catch (uploadErr) {
            console.error('On-behalf document upload failed:', uploadErr);
          }
        }
      }

      // 4b. Per line: the applicant docs for its provider WO (mapped to created doc ids) = the docs
      // reused for a required slot (re-tagged via overrides) plus the extras still ticked. Reused docs
      // are always included even if unticked in the extras list.
      const lineItemDocIds = {};
      const lineItemDocOverrides = {}; // { lineItemId: { createdDocId: requiredCode } }
      createdLines.forEach(li => {
        const svc = serviceFor(li.service_id || li.serviceId, li.customer_name || li.customerName);
        const customer = validCustomers.find(c => c.name.trim() === (li.customer_name || li.customerName));
        if (!svc || !customer) return;
        const { picks, assignedIds } = providerDocPlan(svc, customer.documents || []);
        // Reused required-slot docs → include + override to the required code.
        const overrides = {};
        Object.entries(picks).forEach(([code, docId]) => {
          const real = uploadedDocId[docId];
          if (real) overrides[real] = code;
        });
        // Extras: applicant docs not assigned to a slot and not deselected.
        const extraIds = (customer.documents || [])
          .filter(d => !assignedIds.has(d.id) && !(svc.providerDeselectedDocIds || []).includes(d.id))
          .map(d => uploadedDocId[d.id])
          .filter(Boolean);
        const reusedIds = Object.keys(overrides);
        lineItemDocIds[li.id] = [...new Set([...extraIds, ...reusedIds])];
        if (reusedIds.length) lineItemDocOverrides[li.id] = overrides;
      });

      // 5. Start the client WO and delegate every task to the provider (provider fields + selected docs + uploads).
      await ApiService.delegateOnBehalfWorkOrder(woId, connectionId, lineItemProviderServices, lineItemFieldValues, delegateFiles, lineItemDocIds, lineItemDocOverrides);

      setSuccess('Work order created for the client and delegated to the provider.');
      setTimeout(() => {
        navigate('/work-orders');
      }, 1500);
    } catch (err) {
      console.error('Error creating on-behalf work order:', err);
      setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to create the on-behalf work order');
      setCreating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount || 0);
  };

  const currentCustomer = customers[activeTab];
  const currentCustomerTotals = currentCustomer ? calculateCustomerTotal(currentCustomer) : { subtotal: 0, taxTotal: 0, grandTotal: 0 };
  // Client custom fields + required documents merged across the applicant's services (deduped).
  const mergedFields = currentCustomer ? mergeClientFields(currentCustomer) : [];
  const mergedDocs = currentCustomer ? mergeClientDocs(currentCustomer) : [];
  const mergedDocCodes = new Set(mergedDocs.map(d => d.code));
  const uploadedDocCodes = new Set((currentCustomer?.documents || []).map(d => d.documentType));
  const extraDropdownTypes = documentTypes.filter(dt => {
    const code = dt.code || dt.id;
    return !mergedDocCodes.has(code) && !uploadedDocCodes.has(code);
  });
  const extraUploadedDocs = (currentCustomer?.documents || []).filter(d => !mergedDocCodes.has(d.documentType));

  // One uploaded-document row — reused by client required-doc slots and the extras list.
  const renderUploadedDoc = (doc) => (
    <ListItem key={doc.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
      <DocIcon sx={{ mr: 2, color: 'secondary.main' }} />
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>{doc.fileName}</Typography>
            <Chip label={doc.documentTypeName} size="small" sx={{ fontSize: '0.65rem' }} />
          </Box>
        }
        secondaryTypographyProps={{ component: 'div' }}
        secondary={
          doc.extracting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CircularProgress size={12} />
              <Typography sx={{ fontSize: fontSize.xs, color: 'info.main' }}>Extracting details…</Typography>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                {`Size: ${formatFileSize(doc.fileSize)}`}
                {doc.documentNumber ? ` · Doc No: ${doc.documentNumber}` : ''}
              </Typography>
              {(doc.issueDate || doc.expiryDate) && (
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                  {[doc.issueDate ? `Issued ${doc.issueDate}` : null, doc.expiryDate ? `Expires ${doc.expiryDate}` : null].filter(Boolean).join(' · ')}
                </Typography>
              )}
            </>
          )
        }
      />
      <ListItemSecondaryAction>
        {doc.file && (
          <IconButton edge="end" size="small" onClick={() => window.open(URL.createObjectURL(doc.file), '_blank')} sx={{ mr: 0.5 }}>
            <DocIcon fontSize="small" color="info" />
          </IconButton>
        )}
        <IconButton edge="end" size="small" onClick={() => handleRemoveDocument(doc.id)}>
          <DeleteIcon fontSize="small" color="error" />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );
  const grandTotals = calculateGrandTotals();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: 'secondary.main' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: isMobile ? 2 : 0 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/work-orders')} sx={{ color: 'secondary.main' }}>
          <BackIcon />
        </IconButton>
        <Typography variant={isSmallMobile ? 'h6' : 'h5'} sx={{ fontWeight: 600, color: 'secondary.main' }}>
          Create Work Order on Behalf
        </Typography>
      </Box>

      {/* Errors surface as a toast so they're seen even when scrolled down the form. */}
      <Snackbar
        open={!!error}
        onClose={(e, reason) => { if (reason !== 'clickaway') setError(null); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ maxWidth: 480 }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Full-page block while a document is extracting — the form must not be edited until it finishes. */}
      <Backdrop
        open={customers.some(c => (c.documents || []).some(d => d.extracting))}
        sx={{ color: '#fff', zIndex: (t) => t.zIndex.modal + 1 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography>Extracting document details… please wait</Typography>
        </Box>
      </Backdrop>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column - Form */}
        <Grid item xs={12} lg={8}>
          {/* Client selector — the connected client TC this WO is created for (always walk-in). */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 1, color: 'secondary.main' }}>
              Client Typing Center
            </Typography>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 2 }}>
              The connected client this work order is created for. The services below are the client's;
              pick a corresponding provider service for each — it is delegated to you on submit.
            </Typography>
            <Autocomplete
              size="small"
              options={clients}
              loading={loadingClients}
              value={selectedConnection}
              onChange={(e, value) => handleSelectConnection(value)}
              getOptionLabel={(o) => (o.client_code ? `${o.client_name} (${o.client_code})` : (o.client_name || ''))}
              isOptionEqualToValue={(o, v) => o.connection_id === v.connection_id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select a connected client"
                  placeholder="Search clients..."
                  size="small"
                  required
                />
              )}
            />
            {selectedConnection && thaklisDisabled && (
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mt: 1 }}>
                This client does not have Thaklis features — document/identifier tracking is off.
              </Typography>
            )}
          </Paper>

          {/* Customer type — organization (one of the client's internal orgs) or walk-in. */}
          {selectedConnection && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 2, color: 'secondary.main' }}>
                Customer
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: customerType === 'organization' ? 2 : 0 }}>
                <Button
                  variant={customerType === 'organization' ? 'contained' : 'outlined'}
                  onClick={() => handleCustomerTypeChange('organization')}
                  sx={{ flex: 1, textTransform: 'none' }}
                >
                  Organization
                </Button>
                {!thaklisDisabled && (
                  <Button
                    variant={customerType === 'walkin' ? 'contained' : 'outlined'}
                    onClick={() => handleCustomerTypeChange('walkin')}
                    sx={{ flex: 1, textTransform: 'none' }}
                  >
                    Walk-in Customer
                  </Button>
                )}
              </Box>
              {customerType === 'organization' && (
                <Autocomplete
                  size="small"
                  options={organizations}
                  loading={loadingOrgs}
                  value={selectedOrganization}
                  onChange={handleOrganizationChange}
                  getOptionLabel={(o) => o.name || ''}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Client's organization" placeholder="Select an organization"
                      size="small" required helperText="One of this client's internal organizations" />
                  )}
                />
              )}
            </Paper>
          )}

          {/* Contact Information */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 1, color: 'secondary.main' }}>
              Contact Information
            </Typography>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 2 }}>
              Common contact details for this work order
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="contact@example.com"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone Number"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="+971 50 xxx xxxx"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Work Order Details */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 2, color: 'secondary.main' }}>
              Work Order Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Visa renewal – Ahmed & 3 staff"
                  inputProps={{ maxLength: 50 }}
                  helperText={`${title.length}/50`}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Order Date"
                  type="date"
                  value={orderDate}
                  onChange={(e) => setOrderDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Expected Completion Date"
                  type="date"
                  value={expectedCompletionDate}
                  onChange={(e) => setExpectedCompletionDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Customer Tabs Section — create mode only. In edit mode the WO update edits header fields
              only; services are managed on the WO detail page and applicants/documents aren't edited here. */}
          {!isEditMode && (
          <Paper sx={{ mb: 3 }}>
            {/* Tab Header */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant={isMobile ? 'scrollable' : 'standard'}
                scrollButtons="auto"
                sx={{
                  minHeight: 48,
                  '& .MuiTab-root': {
                    minHeight: 48,
                    textTransform: 'none',
                    fontSize: fontSize.sm,
                    fontWeight: 500
                  },
                  '& .Mui-selected': {
                    color: 'secondary.main'
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'secondary.main'
                  }
                }}
              >
                {customers.map((customer, index) => (
                  <Tab
                    key={customer.id}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isCustomerComplete(customer) ? (
                          <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        ) : (
                          <PersonIcon sx={{ fontSize: 16 }} />
                        )}
                        <span>{customer.name || `Customer ${index + 1}`}</span>
                        {customer.services.length > 0 && (
                          <Chip
                            label={customer.services.length}
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'primary.main', color: 'white' }}
                          />
                        )}
                      </Box>
                    }
                  />
                ))}
              </Tabs>
              <Button
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={handleAddCustomer}
                sx={{ ml: 1, color: 'secondary.main', whiteSpace: 'nowrap' }}
              >
                {isMobile ? '' : 'Add'}
              </Button>
            </Box>

            {/* Tab Content */}
            {currentCustomer && (
              <Box sx={{ p: 3 }}>
                {/* Customer Info */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 2, color: 'secondary.main' }}>
                      Customer Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Full Name"
                          value={currentCustomer.name}
                          onChange={(e) => handleCustomerChange(currentCustomer.id, 'name', e.target.value)}
                          required
                          placeholder="Enter full name"
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Passport Number"
                          value={currentCustomer.passportNumber}
                          onChange={(e) => handleCustomerChange(currentCustomer.id, 'passportNumber', e.target.value)}
                          placeholder="Optional"
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Emirates ID"
                          value={currentCustomer.emiratesId}
                          onChange={(e) => handleCustomerChange(currentCustomer.id, 'emiratesId', e.target.value)}
                          placeholder="Optional"
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        {customers.length > 1 && (
                          <Button
                            fullWidth
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleRemoveCustomer(currentCustomer.id)}
                            sx={{ height: 40 }}
                          >
                            <DeleteIcon />
                          </Button>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Phone Number"
                          value={currentCustomer.phone}
                          onChange={(e) => handleCustomerChange(currentCustomer.id, 'phone', e.target.value)}
                          placeholder="Optional"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Email"
                          type="email"
                          value={currentCustomer.email}
                          onChange={(e) => handleCustomerChange(currentCustomer.id, 'email', e.target.value)}
                          placeholder="Optional"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Services for this customer — only shown in create mode.
                    In edit mode, services are managed inline on the WO detail page. */}
                {!isEditMode && (
                  <>
                    <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 2, color: 'secondary.main' }}>
                      Services for {currentCustomer.name || 'this Customer'}
                    </Typography>

                    {/* Service Selection Row */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                      {/* KAN-29: internal services on the first tab; Thaklis services on the second,
                          shown only when Thaklis services are enabled in Work Order Settings. */}
                      {showThaklisServices && (
                        <>
                          <Tabs
                            value={serviceTab}
                            onChange={(e, v) => { setServiceTab(v); setSelectedServiceToAdd(null); }}
                            sx={{ mb: 1, minHeight: 36 }}
                          >
                            <Tab label="Internal Services" sx={{ minHeight: 36, py: 0 }} />
                            <Tab label="Thaklis Services" sx={{ minHeight: 36, py: 0 }} />
                          </Tabs>
                          {serviceTab === 1 && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                              These services are provided by Thaklis — adding them means you are planning to give this work to Thaklis.
                            </Alert>
                          )}
                        </>
                      )}
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={5}>
                          <Autocomplete
                            options={
                              showThaklisServices
                                ? availableServices.filter(s => (serviceTab === 0
                                    ? s.type === 'INTERNAL'
                                    : s.type !== 'INTERNAL'))
                                : availableServices.filter(s => s.type === 'INTERNAL')
                            }
                            getOptionLabel={(option) => option.name}
                            value={selectedServiceToAdd}
                            onChange={(e, value) => setSelectedServiceToAdd(value)}
                            size="small"
                            renderInput={(params) => (
                              <TextField {...params} label="Select Service" placeholder="Choose a service..." />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Markup (AED)"
                            type="number"
                            value={markupAmount}
                            onChange={(e) => setMarkupAmount(e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">AED</InputAdornment>
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          {selectedServiceToAdd && (
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Total</Typography>
                              <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: 'secondary.main' }}>
                                {formatCurrency(
                                  (selectedServiceToAdd.service_charge || 0) +
                                  (selectedServiceToAdd.govt_charge || 0) +
                                  (parseFloat(markupAmount) || 0)
                                )}
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddServiceToCustomer}
                            disabled={!selectedServiceToAdd}
                          >
                            Add
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* Services Table */}
                    {currentCustomer.services.length === 0 ? (
                      <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary', textAlign: 'center', py: 3 }}>
                        No services added yet. Select a service above to add.
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ bgcolor: 'action.hover', color: 'text.primary', fontWeight: 600, fontSize: '0.8rem' }}>Service</TableCell>
                              <TableCell sx={{ bgcolor: 'action.hover', color: 'text.primary', fontWeight: 600, fontSize: '0.8rem' }} align="right">Service Chg</TableCell>
                              <TableCell sx={{ bgcolor: 'action.hover', color: 'text.primary', fontWeight: 600, fontSize: '0.8rem' }} align="right">Govt Chg</TableCell>
                              <TableCell sx={{ bgcolor: 'action.hover', color: 'text.primary', fontWeight: 600, fontSize: '0.8rem' }} align="right">Markup</TableCell>
                              <TableCell sx={{ bgcolor: 'action.hover', color: 'text.primary', fontWeight: 600, fontSize: '0.8rem' }} align="right">Tax</TableCell>
                              <TableCell sx={{ bgcolor: 'action.hover', color: 'text.primary', fontWeight: 600, fontSize: '0.8rem' }} align="right">Total</TableCell>
                              <TableCell sx={{ bgcolor: 'action.hover', color: 'text.primary', fontWeight: 600, fontSize: '0.8rem' }} align="center" width={60}></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentCustomer.services.map((item) => {
                              const mkp = item.markup_amount || 0;
                              const taxOnMkp = item.tax_on_markup || 0;
                              return (
                              <TableRow key={item.id} hover>
                                <TableCell>
                                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>{item.service_name}</Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                                    <Chip
                                      label={item.service_type}
                                      size="small"
                                      sx={{ fontSize: '0.65rem', height: 18 }}
                                      color={item.service_type === 'THAKLIS' ? 'primary' : 'default'}
                                    />
                                    {!thaklisDisabled && item.service_type === 'THAKLIS' && (
                                      item.created_ticket ? (
                                        <Chip
                                          icon={<CheckIcon sx={{ fontSize: '0.9rem !important' }} />}
                                          label={item.created_ticket.number || 'Ticket created'}
                                          size="small"
                                          color="success"
                                          onDelete={() => handleRemoveCreatedTicket(currentCustomer.id, item.id)}
                                          sx={{ fontSize: '0.65rem', height: 20, fontWeight: 600 }}
                                        />
                                      ) : canCreateTicket && (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="warning"
                                          startIcon={<TicketIcon sx={{ fontSize: '0.95rem !important' }} />}
                                          onClick={() => handleOpenTicketDialog(item)}
                                          sx={{
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            fontSize: '0.68rem',
                                            py: 0.15,
                                            px: 1,
                                            minHeight: 0,
                                            lineHeight: 1.4,
                                            boxShadow: 2,
                                            '@keyframes ticketPulse': {
                                              '0%, 100%': { boxShadow: '0 0 0 0 rgba(237,108,2,0.55)' },
                                              '50%': { boxShadow: '0 0 0 5px rgba(237,108,2,0)' }
                                            },
                                            animation: 'ticketPulse 1.8s infinite'
                                          }}
                                        >
                                          Create Ticket
                                        </Button>
                                      )
                                    )}
                                  </Box>
                                  {/* Corresponding provider service — required. Its custom fields and
                                      documents are filled in the dialog (below), not inline. */}
                                  <Box sx={{ mt: 0.75 }}>
                                    <Button
                                      size="small"
                                      variant={item.providerService ? 'outlined' : 'contained'}
                                      color={item.providerService ? 'success' : 'warning'}
                                      onClick={() => openProviderPicker(item.id)}
                                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', py: 0.4, px: 1.25, lineHeight: 1.4 }}
                                    >
                                      {item.providerService ? `Provider: ${item.providerService.name}` : 'Select provider service *'}
                                    </Button>
                                  </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.8125rem' }}>{formatCurrency(item.service_charge)}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.8125rem' }}>{formatCurrency(item.govt_charge)}</TableCell>
                                <TableCell align="right">
                                  {mkp > 0 ? (
                                    <Typography sx={{ fontSize: fontSize.sm, color: 'success.main', fontWeight: 500 }}>
                                      {formatCurrency(mkp)}
                                    </Typography>
                                  ) : (
                                    <Typography sx={{ fontSize: fontSize.sm, color: 'text.disabled' }}>—</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography sx={{ fontSize: fontSize.sm }}>{formatCurrency(item.tax_value)}</Typography>
                                  {taxOnMkp > 0 && (
                                    <Typography sx={{ fontSize: '0.7rem', color: 'success.main' }}>
                                      (+{formatCurrency(taxOnMkp)})
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{formatCurrency(item.line_total)}</Typography>
                                  {mkp > 0 && (
                                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                      (Base: {formatCurrency(item.original_line_total)})
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <IconButton size="small" color="error" onClick={() => handleRemoveServiceFromCustomer(item.id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                              );
                            })}
                            {/* Subtotal Row */}
                            <TableRow>
                              <TableCell colSpan={5} align="right" sx={{ fontWeight: 600 }}>
                                Customer Subtotal:
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                                {formatCurrency(currentCustomerTotals.grandTotal)}
                              </TableCell>
                              <TableCell />
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}

                  </>
                )}

                {/* Service Information — client custom fields merged across the applicant's services.
                    A field shared by several services is shown (and entered) once. */}
                {mergedFields.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 0.5, color: 'secondary.main' }}>
                      Service Information
                    </Typography>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 2 }}>
                      Details needed for this applicant&apos;s services. Shared fields are entered once.
                    </Typography>
                    <Grid container spacing={2}>
                      {mergedFields.map((field) => (
                        <Grid item xs={12} sm={field.fieldType === 'TEXTAREA' ? 12 : 4} key={field.fieldId}>
                          {renderFieldInput(field, currentCustomer.fieldValues?.[field.fieldId] ?? '',
                            (val) => handleClientFieldChange(field.fieldId, val))}
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Documents for this applicant — client required docs merged across services (deduped),
                    each an upload slot, then an optional "add another". */}
                <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 0.5, color: 'secondary.main' }}>
                  Documents for {currentCustomer.name || 'this Customer'}
                </Typography>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 2 }}>
                  {mergedDocs.length > 0
                    ? 'Attach the documents these services need. Required documents must be uploaded before submitting.'
                    : 'Upload documents specific to this applicant.'}
                </Typography>

                {mergedDocs.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {mergedDocs.map((reqDoc) => {
                      const docsForType = (currentCustomer.documents || []).filter(d => d.documentType === reqDoc.code);
                      const uploaded = docsForType.length > 0;
                      return (
                        <Paper key={reqDoc.code} variant="outlined"
                          sx={{
                            p: 1.5, mb: 1, borderRadius: 2,
                            borderStyle: uploaded ? 'solid' : 'dashed',
                            borderColor: uploaded ? 'success.main' : (reqDoc.required ? 'warning.main' : 'divider')
                          }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              {uploaded
                                ? <CheckIcon sx={{ fontSize: '1.05rem', color: 'success.main' }} />
                                : <DocIcon sx={{ fontSize: '1.05rem', color: reqDoc.required ? 'warning.main' : 'text.disabled' }} />}
                              <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>
                                {reqDoc.label}
                                {reqDoc.required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
                              </Typography>
                            </Box>
                            <Chip
                              label={uploaded ? 'Uploaded' : (reqDoc.required ? 'Required' : 'Optional')}
                              size="small" variant={uploaded ? 'filled' : 'outlined'}
                              color={uploaded ? 'success' : (reqDoc.required ? 'warning' : 'default')}
                              sx={{ fontSize: '0.62rem', height: 18 }}
                            />
                          </Box>
                          {uploaded ? (
                            <List dense sx={{ py: 0 }}>
                              {docsForType.map(renderUploadedDoc)}
                            </List>
                          ) : (
                            <Box
                              tabIndex={0}
                              onPaste={(e) => handleAddDroppedFiles(e, { code: reqDoc.code, label: reqDoc.label })}
                              onDrop={(e) => { e.preventDefault(); handleAddDroppedFiles(e, { code: reqDoc.code, label: reqDoc.label }); }}
                              onDragOver={(e) => e.preventDefault()}
                              sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                                border: '1px dashed', borderColor: 'divider', borderRadius: 1,
                                px: 1.5, py: 1, outline: 'none', '&:focus': { borderColor: 'primary.main' }
                              }}>
                              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                                Drag a file here, paste a copied image (Ctrl/⌘+V), or use Upload
                              </Typography>
                              <Button variant="outlined" size="small" component="label" startIcon={<UploadIcon />} sx={{ flexShrink: 0 }}>
                                Upload
                                <input type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                  onChange={(e) => handleFileUpload(e, { code: reqDoc.code, label: reqDoc.label })} />
                              </Button>
                            </Box>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                )}

                {extraDropdownTypes.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                    <Typography sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary', mb: 1.5 }}>
                      Add another document
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Document Type</InputLabel>
                          <Select value={selectedDocType} label="Document Type" onChange={(e) => setSelectedDocType(e.target.value)} MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}>
                            {(() => {
                              const { primary, secondary } = splitDocTypes(extraDropdownTypes);
                              const item = (dt) => (
                                <MenuItem key={dt.code || dt.id} value={dt.code || dt.id}>
                                  {dt.label || dt.name || dt.code}
                                </MenuItem>
                              );
                              const out = [];
                              if (primary.length > 0) {
                                out.push(<MenuItem key="_p" disabled sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'primary.main', opacity: '1 !important' }}>PRIMARY DOCUMENTS</MenuItem>);
                                primary.forEach(dt => out.push(item(dt)));
                              }
                              if (secondary.length > 0) {
                                out.push(<MenuItem key="_s" disabled sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', opacity: '1 !important', mt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>SECONDARY DOCUMENTS</MenuItem>);
                                secondary.forEach(dt => out.push(item(dt)));
                              }
                              return out;
                            })()}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Button variant="contained" component="label" fullWidth startIcon={<UploadIcon />} disabled={!selectedDocType}>
                          Upload Document(s)
                          <input type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleFileUpload} />
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          tabIndex={0}
                          onPaste={handleAddDroppedFiles}
                          onDrop={(e) => { setDragActive(false); handleAddDroppedFiles(e); }}
                          onDragOver={(e) => e.preventDefault()}
                          onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                          onDragLeave={() => setDragActive(false)}
                          sx={{
                            border: '1px dashed', borderColor: dragActive ? 'primary.main' : 'divider',
                            bgcolor: dragActive ? 'action.hover' : 'transparent', borderRadius: 1,
                            p: 1.25, textAlign: 'center', outline: 'none', '&:focus': { borderColor: 'primary.main' }
                          }}>
                          <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>
                            {selectedDocType
                              ? 'Drag files here, or click this box and paste a copied image (Ctrl/⌘+V)'
                              : 'Select a document type first, then drag or paste files here'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                {extraUploadedDocs.length > 0 && (
                  <List dense>
                    {extraUploadedDocs.map(renderUploadedDoc)}
                  </List>
                )}

                {mergedDocs.length === 0 && extraUploadedDocs.length === 0 && extraDropdownTypes.length === 0 && (
                  <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary', textAlign: 'center', py: 2 }}>
                    No documents uploaded yet
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
          )}

          {/* Notes */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 2, color: 'secondary.main' }}>
              Notes
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Internal Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={3}
              placeholder="Add any internal notes about this work order..."
            />
          </Paper>
        </Grid>

        {/* Right Column - Summary */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, cursor: 'pointer' }}
                onClick={() => setSummaryExpanded(!summaryExpanded)}
              >
                <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, color: 'secondary.main' }}>
                  Order Summary
                </Typography>
                <IconButton size="small">
                  {summaryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={summaryExpanded}>
                {/* Customer Summaries */}
                {customers.filter(c => c.name.trim()).map((customer, index) => {
                  const totals = calculateCustomerTotal(customer);
                  const complete = isCustomerComplete(customer);
                  return (
                    <Paper
                      key={customer.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        borderColor: complete ? 'success.light' : 'divider',
                        bgcolor: activeTab === index ? 'action.selected' : 'transparent',
                        cursor: 'pointer'
                      }}
                      onClick={() => setActiveTab(index)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {complete ? (
                            <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : (
                            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          )}
                          <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>
                            {customer.name}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: 'secondary.main' }}>
                          {formatCurrency(totals.grandTotal)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                        <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                          {customer.services.length} service{customer.services.length !== 1 ? 's' : ''}
                        </Typography>
                        <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                          {customer.documents.length} doc{customer.documents.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })}

                {customers.filter(c => c.name.trim()).length === 0 && (
                  <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary', textAlign: 'center', py: 2 }}>
                    No customers added yet
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Totals */}
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Total Customers</Typography>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>
                    {customers.filter(c => c.name.trim()).length}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Total Services</Typography>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>
                    {customers.reduce((sum, c) => sum + c.services.length, 0)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Total Documents</Typography>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>
                    {customers.reduce((sum, c) => sum + c.documents.length, 0)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Subtotal</Typography>
                  <Typography sx={{ fontSize: fontSize.sm }}>{formatCurrency(grandTotals.subtotal)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>VAT (5%)</Typography>
                  <Typography sx={{ fontSize: fontSize.sm }}>{formatCurrency(grandTotals.taxTotal)}</Typography>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Box display="flex" justifyContent="space-between">
                  <Typography sx={{ fontSize: fontSize.md, fontWeight: 600 }}>Grand Total</Typography>
                  <Typography sx={{ fontWeight: 700, color: 'secondary.main', fontSize: '1.25rem' }}>
                    {formatCurrency(grandTotals.grandTotal)}
                  </Typography>
                </Box>
              </Collapse>

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={creating ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleCreateWorkOrder}
                disabled={creating || (!isEditMode && customers.every(c => c.services.length === 0))}
                sx={{ mt: 2 }}
              >
                {creating ? 'Creating & delegating...' : 'Create & Delegate'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Missing-identifier confirmation */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Some applicants have no unique identifier</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: fontSize.sm }}>
            One or more applicants have neither a Passport Number nor an Emirates ID. Those applicants
            won't be tracked as Thaklis employees, so their documents won't get expiry reminders.
            You can still create the work order — continue anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">
            Go Back
          </Button>
          <Button onClick={proceedCreateWorkOrder} variant="contained" color="secondary">
            Create Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detected applicant differs from the customer on this tab */}
      <Dialog
        open={pendingMismatches.length > 0}
        onClose={handleKeepSameCustomer}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Different applicant detected</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: fontSize.sm }}>
            The uploaded document belongs to{' '}
            <strong>{pendingMismatches[0]?.extracted?.fullName || 'a different person'}</strong>, but
            this tab is for <strong>{pendingMismatches[0]?.existingName}</strong>.
          </DialogContentText>
          <DialogContentText sx={{ fontSize: fontSize.sm, mt: 1.5 }}>
            Do you want to keep this document with <strong>{pendingMismatches[0]?.existingName}</strong>,
            or create a new customer for{' '}
            <strong>{pendingMismatches[0]?.extracted?.fullName || 'the detected applicant'}</strong> and
            move the document there?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleKeepSameCustomer} color="inherit">
            Keep with {pendingMismatches[0]?.existingName || 'current customer'}
          </Button>
          <Button onClick={handleMoveToNewCustomer} variant="contained" color="secondary">
            Create new customer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blocking full-page loader — stays up for the whole create chain
          (work order → document uploads → Thaklis tracking) until the redirect. */}
      <Backdrop
        open={creating}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: '#fff' }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Reset confirmation — client, customer-type, or organization change wipes entered data */}
      <Dialog open={!!pendingReset} onClose={() => setPendingReset(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{pendingReset?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: fontSize.sm }}>{pendingReset?.message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPendingReset(null)} color="inherit">Keep Current</Button>
          <Button onClick={() => { pendingReset?.apply?.(); setPendingReset(null); }} variant="contained" color="error">Change &amp; Clear</Button>
        </DialogActions>
      </Dialog>

      {/* Corresponding provider-service picker (per service line): choose the provider service, then
          fill its custom fields + documents here. */}
      <Dialog open={providerPicker.open} onClose={closeProviderPicker} maxWidth="sm" fullWidth>
        <DialogTitle>Provider service</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: fontSize.sm, mb: 2 }}>
            Choose the service this provider will perform, then fill its details and documents.
          </DialogContentText>
          <Autocomplete
            options={providerServices}
            getOptionLabel={(o) => o.name || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={pickerDraft.service}
            onChange={(e, value) => pickerSelectService(value)}
            renderInput={(params) => (
              <TextField {...params} autoFocus label="Provider service" placeholder="Search provider services..." size="small" />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>{option.name}</Typography>
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                    {formatCurrency((option.service_charge || 0) + (option.govt_charge || 0) + (option.tax_on_service_charge || 0))}
                  </Typography>
                </Box>
              </li>
            )}
          />

          {pickerDraft.service && (() => {
            const custDocs = currentCustomer?.documents || [];
            const draftLine = { providerService: pickerDraft.service, providerReqPick: pickerDraft.reqPick, providerStagedDocs: pickerDraft.stagedDocs };
            const { picks, assignedIds } = providerDocPlan(draftLine, custDocs);
            const fields = pickerDraft.service.customFields || [];
            const reqs = pickerDraft.service.documentRequirements || [];
            const extras = custDocs.filter(d => !assignedIds.has(d.id));
            return (
              <>
                {/* Provider service custom fields (seeded from the provider's defaults) */}
                {fields.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, mb: 1, color: 'success.main' }}>Service details</Typography>
                    <Grid container spacing={2}>
                      {fields.map((f) => (
                        <Grid item xs={12} sm={f.fieldType === 'TEXTAREA' ? 12 : 6} key={f.fieldId}>
                          {renderFieldInput(f, pickerDraft.fieldValues?.[f.fieldId] ?? '', (val) => pickerSetField(f.fieldId, val))}
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Provider required documents — reuse an existing applicant doc or upload a new one. */}
                {reqs.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, mb: 1, color: 'success.main' }}>Documents</Typography>
                    {reqs.map((r) => {
                      const pickId = picks[r.code];
                      const pickedDoc = pickId ? custDocs.find(d => d.id === pickId) : null;
                      const staged = pickerDraft.stagedDocs.filter(s => s.documentType === r.code);
                      const covered = !!pickedDoc || staged.length > 0;
                      const available = custDocs.filter(d => !assignedIds.has(d.id) || d.id === pickId);
                      return (
                        <Paper key={r.code} variant="outlined" sx={{ p: 1, mb: 1, borderRadius: 1, borderStyle: covered ? 'solid' : 'dashed', borderColor: covered ? 'success.main' : (r.required ? 'warning.main' : 'divider') }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip size="small" label={covered ? 'Attached' : (r.required ? 'Required' : 'Optional')}
                              color={covered ? 'success' : (r.required ? 'error' : 'default')} sx={{ height: 18, fontSize: '0.62rem' }} />
                            <Typography sx={{ fontSize: fontSize.sm, flexGrow: 1, fontWeight: 500 }}>{r.label}{r.required ? ' *' : ''}</Typography>
                          </Box>
                          {pickedDoc ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Typography sx={{ fontSize: fontSize.xs, flexGrow: 1 }} noWrap>
                                {pickedDoc.documentTypeName || pickedDoc.documentType || pickedDoc.fileName} <em>(existing)</em>
                              </Typography>
                              <IconButton size="small" onClick={() => pickerClearReqDoc(r.code)}><DeleteIcon sx={{ fontSize: '0.95rem' }} /></IconButton>
                            </Box>
                          ) : staged.length > 0 ? (
                            staged.map(d => (
                              <Box key={d.tempId} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Typography sx={{ fontSize: fontSize.xs, flexGrow: 1 }} noWrap>{d.fileName} <em>(new upload)</em></Typography>
                                <IconButton size="small" onClick={() => pickerRemoveStaged(d.tempId)}><DeleteIcon sx={{ fontSize: '0.95rem' }} /></IconButton>
                              </Box>
                            ))
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                              {available.length > 0 && (
                                <FormControl size="small" sx={{ minWidth: 190 }}>
                                  <Select displayEmpty value="" onChange={(e) => pickerPickReqDoc(r.code, e.target.value)}>
                                    <MenuItem value="" disabled>Use an existing document…</MenuItem>
                                    {available.map(d => (
                                      <MenuItem key={d.id} value={d.id}>{d.documentTypeName || d.documentType || d.fileName}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                              <Button size="small" component="label" startIcon={<UploadIcon fontSize="small" />} sx={{ textTransform: 'none' }}>
                                Upload new
                                <input hidden type="file" onChange={(e) => { pickerStageDoc(r.code, e.target.files); e.target.value = ''; }} />
                              </Button>
                            </Box>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                )}

                {/* Other applicant documents to also send to this provider WO (reused docs excluded). */}
                {extras.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 0.5 }}>Include other applicant documents:</Typography>
                    {extras.map((d) => (
                      <Box key={d.id} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox size="small" sx={{ p: 0.25 }}
                          checked={!pickerDraft.deselectedDocIds.includes(d.id)}
                          onChange={() => pickerToggleExtraDoc(d.id)} />
                        <Typography sx={{ fontSize: fontSize.xs }}>{d.documentTypeName || d.documentType || d.fileName}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeProviderPicker} color="inherit">Cancel</Button>
          <Button onClick={saveProviderPicker} variant="contained" disabled={!pickerDraft.service}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* In-place ticket creation for a Thaklis service line */}
      <EmbeddedTicketDialog
        open={ticketDialog.open}
        onClose={handleCloseTicketDialog}
        mappingId={selectedMappedOrg?.mappingId}
        serviceId={ticketDialog.serviceItem?.service_id}
        serviceName={ticketDialog.serviceItem?.service_name}
        orgName={selectedMappedOrg?.name}
        prefillName={ticketDialog.prefillName}
        onCreated={handleTicketCreated}
      />
    </Box>
  );
};

export default CreateOnBehalfWorkOrder;
